/** Shared constants for licensing components. */

import type { StatusPillTone } from "@/components/ui/status-pill";
import type { ProjectStatus } from "@/types/license-project";
import type {
  LicenseCoverageGapPayload,
  LicenseDealStatus,
  LicenseRequestStatus,
  OffPlatformDeclaration,
} from "@/types/license-request";
import { deriveCoverageStatus } from "@/components/licensing/deal-console/lib/coverageDisplay";
import {
  Clock,
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";

export const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (\u20ac)" },
  { value: "GBP", label: "GBP (\u00a3)" },
  { value: "INR", label: "INR (\u20b9)" },
  { value: "CAD", label: "CAD (C$)" },
  { value: "AUD", label: "AUD (A$)" },
  { value: "JPY", label: "JPY (\u00a5)" },
  { value: "CHF", label: "CHF" },
  { value: "SEK", label: "SEK (kr)" },
  { value: "NOK", label: "NOK (kr)" },
  { value: "DKK", label: "DKK (kr)" },
  { value: "NZD", label: "NZD (NZ$)" },
  { value: "SGD", label: "SGD (S$)" },
  { value: "HKD", label: "HKD (HK$)" },
  { value: "CNY", label: "CNY (\u00a5)" },
  { value: "KRW", label: "KRW (\u20a9)" },
  { value: "BRL", label: "BRL (R$)" },
  { value: "MXN", label: "MXN (MX$)" },
  { value: "ZAR", label: "ZAR (R)" },
  { value: "AED", label: "AED" },
  { value: "SAR", label: "SAR" },
] as const;

export type CurrencyCode = (typeof CURRENCY_OPTIONS)[number]["value"];

/**
 * ONE vocabulary for a project's stage (decision 10 (Raghav, 2026-10-20)): Shortlisting → Awaiting
 * sellers → Negotiating → Licensed, plus Archived. Every surface that names a stage reads it from
 * here: the list tabs and cards, Home, the add-to-project picker, the stage track, the Activity
 * log, the workspace header and the filters. The stored status values do not change; `draft` and
 * `active` are one stage to a buyer (the backend promotes draft → active on the first track). The
 * backend words its sentences the same way (services/project_stages.py STAGE_LABELS).
 */
export const PROJECT_STAGE_LABEL: Record<ProjectStatus, string> = {
  draft: "Shortlisting",
  active: "Shortlisting",
  submitted: "Awaiting sellers",
  negotiating: "Negotiating",
  completed: "Licensed",
  archived: "Archived",
};

/** A stored status in the stage words; an unknown value is humanised, never printed raw. */
export function projectStageLabel(status: string | null | undefined): string {
  const key = String(status ?? "").trim();
  if (!key) return "";
  if (Object.prototype.hasOwnProperty.call(PROJECT_STAGE_LABEL, key)) return PROJECT_STAGE_LABEL[key as ProjectStatus];
  const words = key.replace(/_+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * How a project's status reads to a buyer: the stage word (PROJECT_STAGE_LABEL) and its tone.
 * `tone` drives the StatusPill; `className` stays for the older badge surfaces.
 */
export const PROJECT_STATUS_CONFIG: Record<
  ProjectStatus,
  { label: string; tone: StatusPillTone; className: string }
> = {
  draft: { label: PROJECT_STAGE_LABEL.draft, tone: "neutral", className: "bg-muted/50 text-muted-foreground border-border" },
  active: { label: PROJECT_STAGE_LABEL.active, tone: "neutral", className: "bg-blue-900/30 text-blue-400 border-blue-700/50" },
  submitted: { label: PROJECT_STAGE_LABEL.submitted, tone: "warning", className: "bg-yellow-900/30 text-yellow-400 border-yellow-700/50" },
  negotiating: { label: PROJECT_STAGE_LABEL.negotiating, tone: "accent", className: "bg-purple-900/30 text-purple-400 border-purple-700/50" },
  completed: { label: PROJECT_STAGE_LABEL.completed, tone: "success", className: "bg-green-900/30 text-green-400 border-green-700/50" },
  archived: { label: PROJECT_STAGE_LABEL.archived, tone: "neutral", className: "bg-muted/30 text-muted-foreground border-border" },
};

/**
 * Where a project's status sits on the Active → Submitted → Negotiating → Completed bar, shared by
 * the projects list and the project header so the two cannot place one project differently.
 *
 * `completed` (100) is reached only when the backend proves every accepted track's licence is
 * finished (requests.py::derive_project_status), not when terms are accepted. `archived` has no
 * position: archiving says nothing about how far the project got, so no bar is drawn rather than a
 * full one. (The list drew archived at the Completed stop while the header drew nothing.)
 */
export const PROJECT_STATUS_PROGRESS: Record<ProjectStatus, number | null> = {
  draft: 0,
  active: 0,
  submitted: 33,
  negotiating: 66,
  completed: 100,
  archived: null,
};

/**
 * A project's status as a list surface (the projects list, the home panel, the add-to-project
 * picker) states it: the one stage vocabulary (decision 10). `negotiating` reads "Negotiating" here
 * too; it used to read "In progress" on lists and "Terms accepted" in the header once requests were
 * decided, three words for one stage.
 */
export const projectListStatusLabel = projectStageLabel;

export const REQUEST_STATUS_CONFIG: Record<
  LicenseRequestStatus,
  { label: string; icon: typeof Clock; color: string; badgeClass: string }
> = {
  pending: {
    label: "Pending",
    icon: Clock,
    color: "text-yellow-500",
    badgeClass: "bg-yellow-900/30 text-yellow-400 border-yellow-700/50",
  },
  negotiating: {
    label: "Negotiating",
    icon: MessageSquare,
    color: "text-blue-500",
    badgeClass: "bg-blue-900/30 text-blue-400 border-blue-700/50",
  },
  accepted: {
    label: "Accepted",
    icon: CheckCircle,
    color: "text-green-500",
    badgeClass: "bg-green-900/30 text-green-400 border-green-700/50",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    color: "text-red-500",
    badgeClass: "bg-red-900/30 text-red-400 border-red-700/50",
  },
  withdrawn: {
    label: "Withdrawn",
    icon: AlertCircle,
    color: "text-muted-foreground",
    badgeClass: "bg-muted/30 text-muted-foreground border-border",
  },
};

/**
 * What a track DEAL's stage is called on screen — the one list every surface reads (the track card
 * chip, the deal console badge). Labels state the stage only, never whose move it is: several
 * stages can wait on different parties (a deal in `pending_rights` may wait on a holder to join
 * or on the coordinator's own setup), and the per-track next-step line is what names the actor.
 *
 * `approved` is the trap this list exists to close. What it proves is narrow: every LIVE lane on
 * the platform has finalized (rollup.py::_derive_deal_rollup_from_units — every active clearance
 * unit is `approved`) and nothing has been accepted yet (that is `buyer_accepted`). It does not
 * prove:
 *   - whose move it is. A hard coverage gap or a holder who owes their agreement again after a
 *     deal-terms change (termDisplay.ts::deriveDealAccept) keeps the buyer's Accept refused while
 *     the stage stays `approved`. That is why it is not "Awaiting buyer".
 *   - that BOTH sides are done. A side with no holder has no lane, so the rollup never considers it;
 *     coverage.py::_compute_coverage_gap_payload reports that same deal as `missing_holder_side`.
 *     That is why it is not "All sides finalized".
 * So the label is the bare stage. `dealStageLabel` below refines it from the deal's own data, and
 * the coverage chip and next-step line carry the rest. A deal is only "Licensed" once `fulfilled`;
 * accepting terms is not a licence while contract, payment and delivery are still to come.
 */
export const DEAL_STATUS_LABEL: Record<LicenseDealStatus, string> = {
  pending_rights: "Waiting to start",
  ready_for_units: "Waiting to start",
  negotiating: "Negotiating",
  approved: "Finalized",
  buyer_accepted: "Accepted",
  contracting: "Contracts",
  paid: "Paid",
  fulfilled: "Licensed",
  cancelled: "Cancelled",
};

/**
 * The label for a deal stage; an unknown value is humanised rather than printed as a raw enum.
 *
 * OWN keys only. `key in DEAL_STATUS_LABEL` is also true for every name the object inherits
 * ("toString", "constructor", "__proto__"), so a status carrying one of those returned a FUNCTION
 * (or Object.prototype) where every caller renders a string.
 */
export function dealStatusLabel(status: string | null | undefined): string {
  const key = String(status ?? "").trim();
  if (!key) return "";
  if (Object.prototype.hasOwnProperty.call(DEAL_STATUS_LABEL, key)) {
    return DEAL_STATUS_LABEL[key as LicenseDealStatus];
  }
  const words = key.replace(/_+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The fields `dealStageLabel` reads — present on both the request-detail `LicenseDeal` and the
 *  console's `DealConsoleDeal`, so one rule serves the track card and the console badge. */
export interface DealStageInput {
  status: string | null | undefined;
  buyer_decision_status?: string | null;
  coverage_gaps?: LicenseCoverageGapPayload | null;
  off_platform_declarations?: OffPlatformDeclaration[] | null;
  clearance_units?: ReadonlyArray<{
    status?: string | null;
    approvers?: ReadonlyArray<{ deal_terms_approval_status?: string | null }> | null;
  }> | null;
}

/** What still stands between an `approved` deal and the buyer's Accept, as far as the payload shows. */
export type ApprovedStageBlocker = "coverage" | "agreements";

/** The words for each blocker — the same ones the console badge uses for the same state. */
export const APPROVED_STAGE_BLOCKER_LABEL: Record<ApprovedStageBlocker, string> = {
  coverage: "Coverage incomplete",
  agreements: "Finalizing",
};

// Mirrors termDisplay.ts::isUnitClosed — a declined or cancelled lane counts for nothing.
const CLOSED_LANE_STATUSES: ReadonlySet<string> = new Set(["declined", "cancelled"]);

/**
 * Why an `approved` deal cannot be accepted yet, or null when the payload shows nothing in the way.
 * Same two gates the Accept button obeys (termDisplay.ts::deriveDealAccept):
 *
 *   coverage   — a HARD coverage gap (share % not entered, coverage flagged uncertain, shares over
 *                100%). The buyer cannot declare these away (coverage.py::is_declarable_off_platform_gap).
 *   agreements — a live lane's approver has not agreed the current deal terms
 *                (`deal_terms_approval_status`), or no approver is visible at all. A deal-terms change
 *                resets every approver (negotiation.py::_reset_deal_term_approvals_for_active_units)
 *                and leaves the stage `approved`.
 *
 * A declarable gap (a side with no holder, or one covered only in part) is NOT a blocker: the buyer
 * clears it off-platform inside the Accept modal. A non-buyer's payload is scoped to its own lanes,
 * so `null` there means "nothing in view blocks it", never "the buyer can accept".
 */
export function approvedStageBlocker(deal: DealStageInput): ApprovedStageBlocker | null {
  if (deriveCoverageStatus(deal).hardGaps.length > 0) return "coverage";
  const approvers = (deal.clearance_units ?? [])
    .filter((unit) => !CLOSED_LANE_STATUSES.has(String(unit.status ?? "")))
    .flatMap((unit) => unit.approvers ?? []);
  if (approvers.length === 0) return "agreements";
  if (approvers.some((approver) => approver.deal_terms_approval_status !== "approved")) {
    return "agreements";
  }
  return null;
}

/**
 * The stage label for a deal when the caller has the deal itself, not only its status.
 *
 * Every stage but `approved` is `dealStatusLabel`. `approved` is refined from what the data proves:
 * a recorded acceptance whose stage has not moved yet reads "Accepted"; a blocker reads its own
 * words (`APPROVED_STAGE_BLOCKER_LABEL`); otherwise the bare stage, "Finalized". Never "All sides
 * finalized": a side with no holder at all still reaches `approved` (see DEAL_STATUS_LABEL).
 */
export function dealStageLabel(deal: DealStageInput): string {
  const status = String(deal.status ?? "").trim();
  if (status !== "approved") return dealStatusLabel(status);
  if (deal.buyer_decision_status === "accepted") return dealStatusLabel("buyer_accepted");
  const blocker = approvedStageBlocker(deal);
  return blocker ? APPROVED_STAGE_BLOCKER_LABEL[blocker] : dealStatusLabel(status);
}
